import { useEffect, useState, type JSX } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AuthError } from '@yuan-math/auth';
import { Card } from '@yuan-math/ui';
import { useAuth } from '../state/AuthContext.tsx';
import { AccountsUnavailable } from './RegisterPage.tsx';

/**
 * Where a guardian lands from the consent email (§29).
 *
 * The guardian is not a user of the platform and is not signed in. They arrive
 * from an email, holding a signed token, and should be finished in one click —
 * so this page asks nothing and explains everything.
 */
export function ConsentPage(): JSX.Element {
  const [params] = useSearchParams();
  const { grantConsent, configured } = useAuth();
  const token = params.get('token');

  const [state, setState] = useState<'working' | 'done' | 'failed' | 'missing'>(token ? 'working' : 'missing');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token || !configured) return;
    let cancelled = false;

    const run = async (): Promise<void> => {
      try {
        await grantConsent(token);
        if (!cancelled) setState('done');
      } catch (cause) {
        if (cancelled) return;
        setError(cause instanceof AuthError ? cause.message : 'That link could not be used.');
        setState('failed');
      }
    };
    void run();

    return () => {
      cancelled = true;
    };
  }, [token, configured, grantConsent]);

  if (!configured) return <AccountsUnavailable />;

  if (state === 'working') {
    return (
      <div className="page page--narrow page--centred">
        <h1 className="page__title">Recording your permission…</h1>
      </div>
    );
  }

  if (state === 'done') {
    return (
      <div className="page page--narrow">
        <h1 className="page__title">Thank you — that is all we needed.</h1>
        <p className="page__lead">
          Permission recorded. Your child's progress will now be saved to their account, so it follows them between
          devices.
        </p>

        <Card className="privacy-card">
          <h2 className="parent-card__title">What we hold</h2>
          <ul className="prose-list">
            <li>A name they chose, and an age range — not a birthday.</li>
            <li>Their own work: lessons opened, questions answered, and how each went.</li>
            <li>Your email address, used only for this permission.</li>
          </ul>
          <p className="page__note">
            No school, no location, and no advertising identifiers. Visits are counted with Cloudflare Web
            Analytics, which sets no cookies and cannot follow anyone between sites. You can withdraw permission
            and delete everything at any time from the account page.
          </p>
        </Card>

        <div className="button-row">
          <Link to="/privacy" className="btn btn--secondary">Read the privacy notice</Link>
          <Link to="/" className="btn btn--ghost">See what they are learning</Link>
        </div>
      </div>
    );
  }

  if (state === 'missing') {
    return (
      <div className="page page--narrow">
        <h1 className="page__title">This page needs a permission link</h1>
        <p className="page__lead">
          Follow the link in the email we sent you. If you cannot find it, ask your child to request a new one from
          their account page.
        </p>
        <Link to="/" className="btn btn--secondary">Go to the home page</Link>
      </div>
    );
  }

  return (
    <div className="page page--narrow">
      <h1 className="page__title">That link did not work</h1>
      <p className="page__lead">{error}</p>
      <Card>
        <p>
          Permission links expire after seven days and can be used once. Your child can request a new one from their
          account page, and it will arrive at this address.
        </p>
      </Card>
      <Link to="/" className="btn btn--secondary">Go to the home page</Link>
    </div>
  );
}
